import moment from 'moment'
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects'

import { localeSelector } from '@i18n/selectors'
import { languageKeyToText } from '@i18n/utils'
import { indicateProgress } from 'api'

import { userSelector } from 'auth/selectors'
import featureToggles from 'shared/featureToggles'
import { THINX_TIME_FORMAT } from 'shared/time-utilites'
import { languageFromPath } from 'shared/country'
import { formatLocale } from 'shared/utils'

import {
    ADDRESS_TYPE_BILLING,
    ADDRESS_TYPE_SHIPPING,
    CONTACT_TYPE_BILLING,
    CONTACT_TYPE_SHIPPING,
} from 'dashboard-subscriptions/constants'
import { DELIVERY_COLLECT } from 'subscription-flow/constants'
import * as actions from './actions'
import * as c from './constants'
import * as nAmApi from './nAmApi'
import {
    locationSelector,
    locationSummarySelector,
    productsSelector,
    selectedProductsSelector,
    purchaseDataSelector,
} from './selectors'
import { preferredLocaleSelector } from 'wordpress/selectors'

import { formatDate, getCredentialDistribution, mapVehicles } from './helpers'

export function* getLocationByIdSaga({ locationId }) {
    try {
        yield indicateProgress([c.SUBSCRIPTION_LOCATION_PENDING], function* () {
            const locale = yield select(localeSelector)
            const result = yield call(nAmApi.getSubscriptionByLocation, locationId, null, null, locale)
            yield put(actions.setSubscriptionLocation(result))
        })
    } catch (e) {}
}

export function* getLocationRatesSaga({ startDate, promoCode = '' }) {
    try {
        const formattedStartData = (startDate?.value || moment()).utc().startOf('day').toISOString()

        yield indicateProgress([c.LOCATION_RATES_PENDING], function* () {
            const {
                place: { code: locationCode },
            } = yield select(locationSelector) || {}

            const locale = yield select(localeSelector)

            // refetch location without triggering pending
            const location = yield call(
                nAmApi.getSubscriptionByLocation,
                locationCode,
                formattedStartData,
                promoCode,
                locale
            )
            yield put(actions.setSubscriptionLocation(location))

            const products = yield select(productsSelector)

            const updatedProducts = []
            const waitListProducts = []
            let hasPromoPrice = false

            if (products) {
                let index = 0
                while (index < products.length) {
                    const product = products[index]
                    const updateProduct = { ...product }
                    let newRates
                    if (product.available) {
                        newRates = yield call(
                            nAmApi.getLocationProductRates,
                            locationCode,
                            formattedStartData,
                            product.prdctCd,
                            promoCode,
                            locale
                        )
                    }

                    const isWaitListProduct = Boolean(newRates?.waitListProducts?.[0])

                    const updatedProduct = newRates?.products?.[0] || newRates?.waitListProducts?.[0]

                    updateProduct.ratePlans =
                        updatedProduct?.ratePlans.sort((a, b) => a.price - b.price) || product.ratePlans

                    if (!hasPromoPrice && promoCode) {
                        hasPromoPrice = Boolean(updatedProduct?.ratePlans.find(rate => rate.promoCode === promoCode))
                    }

                    if (!isWaitListProduct) {
                        updatedProducts.push(updateProduct)
                    } else {
                        waitListProducts.push(updateProduct)
                    }

                    index++
                }
            }

            if (promoCode && !hasPromoPrice) {
                hasPromoPrice = new Error('Invalid code')
            }

            yield put(actions.setLocationRates(updatedProducts, waitListProducts, hasPromoPrice))
        })
    } catch (e) {}
}

export function* getOrderSummarySaga({ data }) {
    try {
        yield indicateProgress([c.SUBSCRIPTION_LOCATION_PENDING], function* () {
            const { token } = yield select(userSelector)
            const { product } = data
            const { quantity } = product

            // yes, typo is intentional for api
            const locationSumary = yield select(locationSummarySelector)

            const products = yield select(selectedProductsSelector(product))

            const { delivery } = yield select(purchaseDataSelector)

            const submitData = {
                locationSumary,
                effectiveDate: moment(data.startDate.value).set({ hour: 0 }).format(THINX_TIME_FORMAT),
                type: 'EVERGREEN',
                products,
                members: new Array(quantity).fill({
                    unoccupied: true,
                    tokens: delivery?.accessId && [
                        {
                            accessId: delivery.accessId,
                            typeCode: delivery.typeCode,
                        },
                    ],
                    products,
                }),
                initialInterval: 'EVERGREEN',
            }

            const result = yield call(nAmApi.getOrderSummary, submitData, token)

            if (result) {
                // we don't support multiple products purchase at the moment
                yield put(actions.setOrderSummary(result.invoices[0]))
            }
        })
    } catch (e) {
        throw new Error('General error occurred')
    }
}

export function* getTermsAndConditionsSaga() {
    try {
        yield indicateProgress([c.TERMS_CONDITIONS_PENDING], function* () {
            const { id } = yield select(locationSummarySelector)
            const language = yield select(localeSelector)
            // Get location specific terms and conditions
            let result = yield call(nAmApi.getTermsConditions, id)

            const formattedLang = languageKeyToText(language)
            const termsExist = result?.find(terms => languageKeyToText(terms.locale) === formattedLang)

            if (!result || !termsExist) {
                // Get general terms and conditions
                result = yield call(nAmApi.getTermsConditions)
            }

            yield put(
                actions.setTermsAndConditions(
                    result?.find(terms => languageKeyToText(terms.locale) === formattedLang) || {}
                )
            )
        })
    } catch (e) {
        //TODO Think of a way to add translated error in sagas
        throw new Error('General error occurred')
    }
}

export function* getCollectAddressSaga() {
    try {
        yield indicateProgress([c.GET_COLLECT_ADDRESS_PENDING], function* () {
            const { id } = yield select(locationSummarySelector)
            const locale = yield select(localeSelector)
            const { token } = yield select(userSelector)

            const result = yield call(nAmApi.getCollectAddress, token, id, locale)
            const summaryAddress = yield call(nAmApi.getSummaryAddress, token, id, locale)

            yield put(actions.setCollectAddress(result))
            yield put(actions.setSummaryAddress(summaryAddress))
        })
    } catch (e) {
        //TODO Think of a way to add translated error in sagas
        throw new Error('General error occurred')
    }
}

export function* buySubscriptionSaga({ data }) {
    try {
        yield indicateProgress({ names: [c.SUBSCRIPTION_PURCHASE_PENDING], stopException: true }, function* () {
            const {
                delivery,
                product,
                paymentMethod,
                deliveryAddress,
                billingAddress,
                termsAcceptedDate,
                vehicles,
                sendShipChargeId,
            } = data
            const { quantity } = product
            const { queryParams = '' } = delivery?.rate || {}
            const { token, accountKey, details } = yield select(userSelector)
            const locationSumary = yield select(locationSummarySelector)
            const products = yield select(selectedProductsSelector(product))
            const preferredLocale = yield select(preferredLocaleSelector)

            const contacts = [
                {
                    firstName: details.firstName,
                    lastName: details.lastName,
                    email: details.emailAddress,
                    contactType: CONTACT_TYPE_SHIPPING,
                    address: {
                        addressee: deliveryAddress.addressee || `${details.firstName} ${details.lastName}`,
                        line1: deliveryAddress.address1,
                        line2: deliveryAddress.address2,
                        city: deliveryAddress.city,
                        country: deliveryAddress.country,
                        state: deliveryAddress.state,
                        zip: deliveryAddress.postalCode,
                        type: ADDRESS_TYPE_SHIPPING,
                    },
                },
                {
                    firstName: details.firstName,
                    lastName: details.lastName,
                    email: details.emailAddress,
                    contactType: CONTACT_TYPE_BILLING,
                    address: {
                        addressee: billingAddress.addressee || `${details.firstName} ${details.lastName}`,
                        line1: billingAddress.address1,
                        line2: billingAddress.address2,
                        city: billingAddress.city,
                        country: billingAddress.country,
                        state: billingAddress.state,
                        zip: billingAddress.postalCode,
                        type: ADDRESS_TYPE_BILLING,
                    },
                },
            ]

            const formattedEffectiveDate = formatDate(data.startDate)

            const members = Array.from({ length: quantity }, () => ({
                unoccupied: !featureToggles.hasSubcriptionLite,
                startDate: formattedEffectiveDate,
                tokens: delivery?.accessId && [
                    {
                        accessId: delivery.accessId,
                        typeCode: delivery.typeCode,
                    },
                ],
                products,
                ...(featureToggles.hasSubcriptionLite
                    ? {
                          firstName: details.firstName,
                          lastName: details.lastName,
                      }
                    : {}),
                ...mapVehicles(vehicles),
            }))

            const submitData = {
                accountId: accountKey,
                nickName: `${details.firstName} ${details.lastName}`,
                autoPay: true,
                autoRenewal: true,
                effectiveDate: formattedEffectiveDate,
                initialTerm: 12,
                initialInterval: 'EVERGREEN',
                state: 'ACTIVE',
                type: 'EVERGREEN',
                preferredLocale: preferredLocale,
                extended: {
                    fieldMap: {
                        paymentType: {
                            code: 'PAYMENTTYPE',
                            value: 'SAVEDPAYMENT',
                        },
                        invoiceDeliveryMethod: {
                            code: 'INVDELIVERY',
                            value: 'EMAIL',
                        },
                        credentialDistribution: delivery ? getCredentialDistribution(delivery) : {},
                    },
                },
                paymentMethods: [{ ...paymentMethod?.nAmFields }],
                members,
                contacts,
                products,
                locationSumary,
                termsAcceptedDate,
            }

            yield call(
                nAmApi.purchaseSubscription,
                submitData,
                queryParams,
                preferredLocale,
                { shipChargeId: sendShipChargeId && delivery?.prdctCd !== DELIVERY_COLLECT && delivery?.rate?.rateId },
                token
            )
        })
    } catch (e) {
        //TODO Think of a way to add translated error in sagas
        throw new Error('General error occurred')
    }
}

export function* addRateToWaitListSaga({ data }) {
    try {
        yield indicateProgress({ names: [c.ADD_RATE_TO_WAITLIST_PENDING], stopException: true }, function* () {
            const { rateId, waitListId, startDate } = data
            const { token, accountKey, details } = yield select(userSelector)
            const formattedDate = formatDate(startDate)
            const locale = formatLocale(languageFromPath())

            const requestOwner = {
                firstName: details.firstName,
                lastName: details.lastName,
                email: details.emailAddress,
            }

            yield call(
                nAmApi.addRateToWaitList,
                rateId,
                waitListId,
                accountKey,
                formattedDate,
                requestOwner,
                token,
                locale
            )
        })
    } catch (e) {
        console.error(e)
    }
}

export function* rootSaga() {
    yield takeLatest(c.GET_SUBSCRIPTION_LOCATION, getLocationByIdSaga)
    yield takeLatest(c.GET_LOCATION_RATES, getLocationRatesSaga)
    yield takeLatest(c.GET_ORDER_SUMMARY, getOrderSummarySaga)
    yield takeLatest(c.GET_COLLECT_ADDRESS, getCollectAddressSaga)
    yield takeLatest(c.GET_TERMS_AND_CONDITIONS, getTermsAndConditionsSaga)
    yield takeEvery(c.BUY_SUBSCRIPTION, buySubscriptionSaga)
    yield takeLatest(c.ADD_RATE_TO_WAITLIST, addRateToWaitListSaga)
}
